import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, BarChart3, Heart, Printer, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ActivityDetail = () => {
  const { id } = useParams();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [checkedSteps, setCheckedSteps] = useState([]);
  const { isAuthenticated, token } = useAuth();

  useEffect(() => {
    fetchActivity();
    if (isAuthenticated) {
      checkIfFavorite();
    }
  }, [id, isAuthenticated]);

  const fetchActivity = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/activities/${id}`);
      setActivity(response.data);
    } catch (error) {
      console.error('Error fetching activity:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkIfFavorite = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const favoriteIds = response.data.map(a => a.id);
      setIsFavorite(favoriteIds.includes(id));
    } catch (error) {
      console.error('Error checking favorites:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!isAuthenticated) {
      alert('Sign in to save favorites!');
      return;
    }

    try {
      if (isFavorite) {
        await axios.delete(`${API_URL}/api/user/favorites/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/api/user/favorites/${id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setIsFavorite(!isFavorite);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-teal-500" />
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Activity not found</h2>
          <Link to="/activities">
            <Button>Back to Activities</Button>
          </Link>
        </div>
      </div>
    );
  }

  const toggleStep = (index) => {
    setCheckedSteps(prev => 
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const handlePrint = () => {
    window.print();
  };

  const getDifficultyColor = (difficulty) => {
    switch(difficulty) {
      case 'Easy': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <Link to="/activities">
          <Button variant="ghost" className="mb-6 hover:bg-amber-50">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Activities
          </Button>
        </Link>

        {/* Hero Image & Title */}
        <div className="relative h-96 rounded-2xl overflow-hidden mb-8 shadow-2xl">
          <img 
            src={activity.imageUrl} 
            alt={activity.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Badge className="bg-white text-gray-900">
                {activity.category}
              </Badge>
              <Badge className={getDifficultyColor(activity.difficulty)}>
                {activity.difficulty}
              </Badge>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{activity.title}</h1>
            <div className="flex items-center gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>{activity.duration}</span>
              </div>
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                <span>{activity.ageGroup}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 mb-8">
          <Button
            onClick={toggleFavorite}
            variant="outline"
            className="flex-1 md:flex-none"
          >
            <Heart className={`h-4 w-4 mr-2 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
            {isFavorite ? 'Saved' : 'Save'}
          </Button>
          <Button
            onClick={handlePrint}
            variant="outline"
            className="flex-1 md:flex-none"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print Guide
          </Button>
        </div>

        {/* Description */}
        <Card className="mb-8 border-2">
          <CardHeader>
            <h2 className="text-2xl font-bold">About This Activity</h2>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 text-lg leading-relaxed">
              {activity.detailedDescription}
            </p>
          </CardContent>
        </Card>

        {/* Materials Needed */}
        <Card className="mb-8 border-2">
          <CardHeader>
            <h2 className="text-2xl font-bold">Materials Needed</h2>
          </CardHeader>
          <CardContent>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {activity.materials.map((material, index) => (
                <li key={index} className="flex items-center gap-2 text-gray-700">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-400" />
                  <span>{material}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Step-by-Step Instructions */}
        <Card className="mb-8 border-2">
          <CardHeader>
            <h2 className="text-2xl font-bold">Step-by-Step Instructions</h2>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {activity.steps.map((step, index) => (
                <div 
                  key={index}
                  className="flex gap-4 p-4 rounded-lg hover:bg-amber-50 transition-colors cursor-pointer"
                  onClick={() => toggleStep(index)}
                >
                  <div className="flex-shrink-0">
                    {checkedSteps.includes(index) ? (
                      <CheckCircle2 className="h-6 w-6 text-green-500 fill-green-500" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold">
                        {index + 1}
                      </div>
                    )}
                  </div>
                  <p className={`text-gray-700 flex-1 ${checkedSteps.includes(index) ? 'line-through text-gray-400' : ''}`}>
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Benefits */}
        <Card className="mb-8 border-2">
          <CardHeader>
            <h2 className="text-2xl font-bold">Benefits for Your Child</h2>
          </CardHeader>
          <CardContent>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {activity.benefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">{benefit}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Related Activities CTA */}
        <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white border-0">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Enjoyed this activity?</h3>
            <p className="mb-6 text-white/90">
              Discover more activities tailored to your child's age and interests.
            </p>
            <Link to="/activities">
              <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100">
                Browse More Activities
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ActivityDetail;